// src/pages/msme/SigmaStudioPareto.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faDownload,
  faQuestionCircle,
  faEdit,
  faTrash,
  faTimes,
  faFilter,
  faUndo,
} from "@fortawesome/free-solid-svg-icons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
} from "chart.js";
import { Bar } from "react-chartjs-2";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement
);

// Example data with realistic dates (spread over ~2 months)
const exampleDefects = [
  { id: 1, defect: "Misregistration", count: 320, date: "2026-02-10" },
  { id: 2, defect: "Dot Gain / Dirty Print", count: 250, date: "2026-02-15" },
  { id: 3, defect: "Ghosting", count: 140, date: "2026-02-20" },
  { id: 4, defect: "Pinholing / Pinholes", count: 90, date: "2026-02-25" },
  { id: 5, defect: "Feathering", count: 60, date: "2026-03-02" },
  { id: 6, defect: "Ink Smearing / Set-off", count: 45, date: "2026-03-05" },
  { id: 7, defect: "Bridging / Filling-in", count: 35, date: "2026-03-08" },
  { id: 8, defect: "Others (misc)", count: 60, date: "2026-03-10" },
  { id: 9, defect: "Misregistration", count: 180, date: "2026-03-12" },
  { id: 10, defect: "Ghosting", count: 95, date: "2026-03-14" },
];

const SigmaStudioPareto = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [defects, setDefects] = useState([]);
  const [search, setSearch] = useState("");
  const [showWhyModal, setShowWhyModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Date filter states
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [activeFilterFrom, setActiveFilterFrom] = useState("");
  const [activeFilterTo, setActiveFilterTo] = useState("");

  const initialForm = { defect: "", count: "", date: new Date().toISOString().split("T")[0] };
  const [formData, setFormData] = useState(initialForm);

  // ─── FILTERED DATA ──────────────────────────────────────────────────
  const filteredByDate = defects.filter((d) => {
    if (!activeFilterFrom && !activeFilterTo) return true;
    const itemDate = new Date(d.date);
    const from = activeFilterFrom ? new Date(activeFilterFrom) : null;
    const to = activeFilterTo ? new Date(activeFilterTo) : null;

    if (from && itemDate < from) return false;
    if (to && itemDate > to) return false;
    return true;
  });

  const filtered = filteredByDate
    .filter((d) => d.defect.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => Number(b.count) - Number(a.count));

  // ─── CALCULATIONS ───────────────────────────────────────────────────
  const total = filtered.reduce((sum, d) => sum + Number(d.count || 0), 0);
  let cumulative = 0;

  const chartData = filtered.map((item) => {
    cumulative += Number(item.count || 0);
    return {
      ...item,
      percentage: total > 0 ? ((Number(item.count) / total) * 100).toFixed(1) : "0.0",
      cumulativePct: total > 0 ? ((cumulative / total) * 100).toFixed(1) : "0.0",
    };
  });

  const chartConfig = {
    labels: chartData.map((d) => `${d.defect} (${d.date})`),
    datasets: [
      {
        type: "bar",
        label: "Defect Count",
        data: chartData.map((d) => d.count),
        backgroundColor: "rgba(59, 130, 246, 0.65)",
        borderColor: "rgb(59, 130, 246)",
        borderWidth: 1,
      },
      {
        type: "line",
        label: "Cumulative %",
        data: chartData.map((d) => d.cumulativePct),
        borderColor: "rgb(234, 179, 8)",
        backgroundColor: "rgba(234, 179, 8, 0.15)",
        borderWidth: 3,
        pointBackgroundColor: "rgb(234, 179, 8)",
        pointBorderColor: "#111827",
        pointRadius: 5,
        tension: 0.3,
        yAxisID: "yPct",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top", labels: { color: "#e5e7eb" } },
      title: {
        display: true,
        text: `Pareto Chart – Label Defects (${filtered.length} entries shown)`,
        color: "#fef08a",
        font: { size: 18 },
      },
      tooltip: {
        callbacks: {
          label: (ctx) =>
            ctx.dataset.type === "bar"
              ? `${ctx.parsed.y} pcs (${ctx.raw.percentage}%) – ${ctx.raw.date}`
              : `Cumulative: ${ctx.parsed.y}%`,
        },
      },
    },
    scales: {
      y: {
        position: "left",
        title: { display: true, text: "Count", color: "#9ca3af" },
        ticks: { color: "#9ca3af" },
        grid: { color: "rgba(75,85,99,0.3)" },
      },
      yPct: {
        position: "right",
        title: { display: true, text: "Cumulative %", color: "#fcd34d" },
        ticks: { color: "#fcd34d", callback: (v) => v + "%" },
        min: 0,
        max: 100,
        grid: { drawOnChartArea: false },
      },
      x: {
        ticks: { color: "#9ca3af", maxRotation: 50, minRotation: 50, font: { size: 11 } },
        grid: { display: false },
      },
    },
  };

  // ─── ACTIONS ────────────────────────────────────────────────────────
  const handlePopulateExample = () => {
    if (defects.length > 0 && !window.confirm("Replace current data with example?")) return;
    setDefects(exampleDefects.map((d) => ({ ...d })));
    // Reset filters when populating example
    setFilterFrom("");
    setFilterTo("");
    setActiveFilterFrom("");
    setActiveFilterTo("");
  };

  const handleAddNew = () => {
    setEditingId(null);
    setFormData(initialForm);
    setShowFormModal(true);
  };

  const handleEdit = (id) => {
    const item = defects.find((d) => d.id === id);
    if (item) {
      setEditingId(id);
      setFormData({ defect: item.defect, count: item.count, date: item.date });
      setShowFormModal(true);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm("Delete this entry?")) {
      setDefects((prev) => prev.filter((d) => d.id !== id));
    }
  };

  const handleSubmitForm = (e) => {
    e.preventDefault();
    const countNum = Number(formData.count);
    if (!formData.defect.trim() || isNaN(countNum) || countNum < 0 || !formData.date) {
      alert("Please fill defect, valid count ≥ 0, and date.");
      return;
    }

    const newItem = {
      id: editingId || Date.now(),
      defect: formData.defect.trim(),
      count: countNum,
      date: formData.date,
    };

    if (editingId) {
      setDefects((prev) => prev.map((d) => (d.id === editingId ? newItem : d)));
    } else {
      setDefects((prev) => [...prev, newItem]);
    }

    setShowFormModal(false);
    setFormData(initialForm);
    setEditingId(null);
  };

  const applyDateFilter = () => {
    setActiveFilterFrom(filterFrom);
    setActiveFilterTo(filterTo);
  };

  const clearDateFilter = () => {
    setFilterFrom("");
    setFilterTo("");
    setActiveFilterFrom("");
    setActiveFilterTo("");
  };

  const handleExport = () => {
    const rows = [
      ["Date", "Defect", "Count", "Percentage", "Cumulative %"],
      ...chartData.map((d) => [d.date, d.defect, d.count, d.percentage, d.cumulativePct]),
    ];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const uri = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    const link = document.createElement("a");
    link.href = uri;
    link.download = `pareto_defects_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-4 md:p-6">
      {/* HEADER */}
      <header className="sticky top-0 z-50 bg-black border-b border-blue-400 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-yellow-200">
            🎛️ Think5Whys Sigma Studio
          </div>
          <div className="text-[11px] text-gray-200">
            Pareto Chart – 80:20 Defect Analysis
          </div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          {isLoggedIn ? (
            <>
              <div className="text-xs lg:text-sm text-teal-300">{userName}</div>
              <button
                onClick={handleLogout}
                className="w-8 px-2 py-1 rounded-md bg-red-600 hover:bg-red-700"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-8 px-2 py-1 rounded-full border border-gray-700 text-sm"
            >
              🔐
            </button>
          )}
        </div>
      </header>

      {/* ACTIONS + DATE FILTER */}
      <div className="mt-6 mb-6 space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <button
            onClick={handlePopulateExample}
            className="px-3 py-2 bg-green-600 hover:bg-green-700 rounded text-sm flex items-center justify-center gap-2"
          >
            <FontAwesomeIcon icon={faPlus} /> Populate Example
          </button>

          <button
            onClick={handleAddNew}
            className="px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded text-sm flex items-center justify-center gap-2"
          >
            <FontAwesomeIcon icon={faPlus} /> Add Entry
          </button>

          <button
            onClick={() => setShowWhyModal(true)}
            className="px-3 py-2 bg-green-700 hover:bg-green-800 rounded text-sm flex items-center justify-center gap-2"
          >
            <FontAwesomeIcon icon={faQuestionCircle} /> Why Pareto?
          </button>

          <button
            onClick={handleExport}
            className="px-3 py-2 bg-blue-700 hover:bg-blue-800 rounded text-sm flex items-center justify-center gap-2"
          >
            <FontAwesomeIcon icon={faDownload} /> Export CSV
          </button>
        </div>

        {/* Date Filter Controls */}
        <div className="bg-gray-800 p-4 rounded border border-gray-700">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-sm mb-1 text-gray-300">From Date</label>
              <input
                type="date"
                value={filterFrom}
                onChange={(e) => setFilterFrom(e.target.value)}
                className="px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm mb-1 text-gray-300">To Date</label>
              <input
                type="date"
                value={filterTo}
                onChange={(e) => setFilterTo(e.target.value)}
                className="px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={applyDateFilter}
                className="w-40 px-4 py-2 bg-teal-600 hover:bg-teal-700 rounded flex items-center gap-2 text-sm"
              >
                <FontAwesomeIcon icon={faFilter} /> Apply Filter
              </button>
              <button
                onClick={clearDateFilter}
                className="w-20 px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded flex items-center gap-2 text-sm"
              >
                <FontAwesomeIcon icon={faUndo} /> Clear
              </button>
            </div>
          </div>
          {(activeFilterFrom || activeFilterTo) && (
            <div className="mt-2 text-sm text-teal-300">
              Showing data from {activeFilterFrom || "beginning"} to {activeFilterTo || "today"}
            </div>
          )}
        </div>
      </div>

      {/* SEARCH */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="Filter by defect name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-md px-4 py-2 rounded bg-gray-800 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* CHART */}
      {filtered.length > 0 ? (
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700 h-96 md:h-[500px] mb-8">
          <Bar data={chartConfig} options={options} />
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500 border border-dashed border-gray-700 rounded-lg">
          No defects in selected date range / search. Add entries or adjust filter.
        </div>
      )}

      {/* LIST */}
      {filtered.length > 0 && (
        <div className="mb-12">
          <h3 className="text-lg font-semibold text-teal-300 mb-3">
            Defect Entries ({filtered.length} shown)
          </h3>

          {/* Desktop Table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full border border-gray-700">
              <thead className="bg-gray-800">
                <tr>
                  <th className="px-4 py-2 text-left">Date</th>
                  <th className="px-4 py-2 text-left">Defect</th>
                  <th className="px-4 py-2 text-center">Count</th>
                  <th className="px-4 py-2 text-center">% Total</th>
                  <th className="px-4 py-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {chartData.map((d) => (
                  <tr key={d.id} className="border-t border-gray-700 hover:bg-gray-700">
                    <td className="px-4 py-2">{d.date}</td>
                    <td className="px-4 py-2">{d.defect}</td>
                    <td className="px-4 py-2 text-center">{d.count}</td>
                    <td className="px-4 py-2 text-center">{d.percentage}%</td>
                    <td className="px-4 py-2 text-center flex justify-center gap-4">
                      <button onClick={() => handleEdit(d.id)} className="text-yellow-400 hover:text-yellow-300">
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button onClick={() => handleDelete(d.id)} className="text-red-500 hover:text-red-400">
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="sm:hidden space-y-4">
            {chartData.map((d) => (
              <div key={d.id} className="bg-gray-800 p-4 rounded border border-gray-700">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="font-medium">{d.defect}</div>
                    <div className="text-sm text-gray-400">{d.date}</div>
                  </div>
                  <div className="flex gap-4">
                    <button onClick={() => handleEdit(d.id)} className="text-yellow-400">
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button onClick={() => handleDelete(d.id)} className="text-red-500">
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
                <div className="text-sm">
                  Count: <strong>{d.count}</strong> • {d.percentage}% (cumul: {d.cumulativePct}%)
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ADD/EDIT MODAL */}
      {showFormModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md border border-gray-700">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-xl font-bold text-yellow-200">
                {editingId ? "Edit Defect Entry" : "Add Defect Entry"}
              </h2>
              <button
                onClick={() => setShowFormModal(false)}
                className="text-gray-400 hover:text-white text-2xl"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <form onSubmit={handleSubmitForm} className="space-y-5">
              <div>
                <label className="block text-sm mb-1">Date</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded"
                  required
                />
              </div>

              <div>
                <label className="block text-sm mb-1">Defect / Problem</label>
                <input
                  type="text"
                  value={formData.defect}
                  onChange={(e) => setFormData({ ...formData, defect: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. Misregistration"
                  required
                />
              </div>

              <div>
                <label className="block text-sm mb-1">Count</label>
                <input
                  type="number"
                  min="0"
                  value={formData.count}
                  onChange={(e) => setFormData({ ...formData, count: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. 320"
                  required
                />
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowFormModal(false)}
                  className="px-5 py-2 bg-gray-700 hover:bg-gray-600 rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 rounded"
                >
                  {editingId ? "Update" : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* WHY MODAL (unchanged) */}
      {showWhyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-xl font-bold text-yellow-200">Why Pareto Chart with Dates?</h2>
              <button
                onClick={() => setShowWhyModal(false)}
                className="text-gray-400 hover:text-white text-2xl"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="space-y-4 text-sm">
              <p>Use date filtering to:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Compare this week vs last week</li>
                <li>See if improvements are sustained</li>
                <li>Focus on recent production batches</li>
                <li>Identify seasonal / shift patterns</li>
              </ul>
              <p className="text-teal-300 mt-4">
                Tip: After applying 5-Why on top defects, add follow-up entries with dates to track reduction.
              </p>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowWhyModal(false)}
                className="px-6 py-2 bg-gray-700 hover:bg-gray-600 rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FOOTER */}
      <footer className="bg-black border-t border-blue-400 text-white py-4 mt-12">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">
            © 2026 Think5Whys Global DeepTech Solutions.
          </div>
          <div className="flex flex-wrap gap-4 text-sm">
            <a href="/about-us" className="hover:text-teal-300">About</a>
            <a href="/privacy-policy" className="hover:text-teal-300">Privacy Policy</a>
            <a href="/terms-of-use" className="hover:text-teal-300">Terms of Use</a>
            <a href="/contact-us" className="hover:text-teal-300">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SigmaStudioPareto;